import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Accordion, Group, ScrollArea, Text } from "@mantine/core";
import { useAppAction, useAppSelector } from "../../hooks/redux.js";
import { selectSource, selectSourceLayer, selectSourceLayerLayer } from "../../reducers/sourcesReducer.js";
import { addHighlightLayers, removeHighlightLayers } from "../../reducers/layersReducer.js";
import { createHighlightLayers } from "../../utils/highlight.js";
import { FormattedMessage } from "react-intl";
import { getFilteredSourcesAndLayers } from "../../utils/selectors.js";
import { memo, useCallback, useEffect } from "react";
import { useCaptureEvent } from "../../components/StyleEditorContext.js";
import { Event } from "../../constants/events.js";
import { filterBy } from "../../utils/dash.js";
import { UsedSourceLayerIcon } from "./UsedSourceLayerIcon.js";
import { LayerListItem } from "../Layers/LayerListItem.js";
import { SourceListHeader } from "./SourceListHeader.js";
import { PrimaryPanel } from "../../components/SidePanel.js";
const accordionItemContentStyle = {
    content: {
        padding: 0,
    },
};
const nestedAccordionControlStyle = {
    label: {
        padding: "4px",
    },
    control: {
        paddingInlineEnd: "8px",
        paddingInlineStart: "8px",
    },
};
const accordionControlStyle = {
    label: {
        padding: "6px",
    },
    control: {
        paddingInlineEnd: "8px",
        paddingInlineStart: "8px",
    },
};
export const VectorSourceAccordionItem = ({ sourceId, selectedSourceLayerId, selectedSourceLayerLayerId, layers, tileJSON, metadata, onSelectSourceLayer, onSelectSourceLayerLayer, }) => {
    if (!tileJSON && !metadata) {
        return (_jsx(Group, { px: 8, h: 30, children: _jsx(Text, { fz: 14, c: "dimmed", children: _jsx(FormattedMessage, { id: "text.loadingLayers", defaultMessage: "Loading layers..." }) }) }));
    }
    const vectorLayers = (metadata && "vectorLayers" in metadata ? metadata.vectorLayers : undefined) ?? tileJSON?.vector_layers ?? [];
    return (_jsxs(Accordion.Item, { value: sourceId, children: [_jsx(Accordion.Control, { styles: accordionControlStyle, children: _jsx(Text, { fz: 14, fw: "bold", children: sourceId }) }), _jsx(Accordion.Panel, { styles: accordionItemContentStyle, children: vectorLayers.map(({ id: sourceLayerId }) => {
                    const sourceLayerLayers = filterBy(layers, "source-layer", sourceLayerId);
                    const isUsed = sourceLayerLayers.length > 0;
                    return (_jsx(Accordion, { variant: "default", value: selectedSourceLayerId, onChange: onSelectSourceLayer, children: _jsxs(Accordion.Item, { value: sourceLayerId, children: [_jsx(Accordion.Control, { styles: nestedAccordionControlStyle, children: _jsxs(Group, { gap: "xs", children: [_jsx(UsedSourceLayerIcon, { isUsed: isUsed }), _jsx(Text, { fz: 14, fw: "600", children: sourceLayerId })] }) }), _jsx(Accordion.Panel, { styles: accordionItemContentStyle, style: { border: "none" }, children: sourceLayerLayers.map(({ id, type }) => {
                                        const selected = id === selectedSourceLayerLayerId;
                                        const handleClick = () => {
                                            onSelectSourceLayerLayer(id);
                                        };
                                        return (_jsx(LayerListItem, { type: type, id: id, selected: selected, onClick: handleClick, indent: 12, style: {
                                                borderRight: "none",
                                                borderLeft: "none",
                                            } }, id));
                                    }) })] }) }, sourceLayerId));
                }) })] }, sourceId));
};
export const RasterSourceAccordionItem = ({ sourceId, }) => {
    return (_jsx(Accordion.Item, { value: sourceId, children: _jsx(Accordion.Control, { styles: accordionControlStyle, children: _jsx(Text, { fz: 14, fw: "bold", children: sourceId }) }) }, sourceId));
};
export const GeoJSONSourceAccordionItem = ({ sourceId, selectedSourceLayerLayerId, layers, metadata, isInline, onSelectSourceLayerLayer, }) => {
    if (!isInline && !metadata) {
        return (_jsx(Group, { px: 8, h: 30, children: _jsx(Text, { fz: 14, c: "dimmed", children: _jsx(FormattedMessage, { id: "text.loadingLayers", defaultMessage: "Loading layers..." }) }) }));
    }
    const tilestatsLayers = metadata && "tilestats" in metadata ? metadata.tilestats.layers : [];
    return (_jsxs(Accordion.Item, { value: sourceId, children: [_jsx(Accordion.Control, { styles: accordionControlStyle, children: _jsx(Text, { fz: 14, fw: "bold", children: sourceId }) }), _jsxs(Accordion.Panel, { styles: accordionItemContentStyle, children: [isInline && (_jsx(Group, { px: 8, py: 4, children: _jsx(Text, { fz: 12, c: "dimmed", fs: "italic", children: _jsx(FormattedMessage, { id: "text.inlineGeoJSON", defaultMessage: "Inline GeoJSON" }) }) })), tilestatsLayers.map(({ layer, count, geometry }) => (_jsxs(Group, { px: 8, py: 4, gap: "xs", children: [_jsx(Text, { fz: 13, fw: "600", children: layer }), _jsxs(Text, { fz: 12, c: "dimmed", children: [Array.isArray(geometry) ? geometry.join(", ") : geometry, " ", "(", count, ")"] })] }, layer))), layers.map(({ id, type }) => {
                        const selected = id === selectedSourceLayerLayerId;
                        const handleClick = () => {
                            onSelectSourceLayerLayer(id);
                        };
                        return (_jsx(LayerListItem, { type: type, id: id, selected: selected, onClick: handleClick, indent: 12 }, id));
                    })] })] }, sourceId));
};
export const SourceList = memo(({ selectedSourceId, selectedSourceLayerId, selectedSourceLayerLayerId, onClose }) => {
    const captureEvent = useCaptureEvent();
    const changeSource = useAppAction(selectSource);
    const changeSourceLayer = useAppAction(selectSourceLayer);
    const changeSourceLayerLayer = useAppAction(selectSourceLayerLayer);
    const sources = useAppSelector(getFilteredSourcesAndLayers);
    const removeHighlight = useAppAction(removeHighlightLayers);
    const addHighlight = useAppAction(addHighlightLayers);
    useEffect(() => {
        return () => {
            removeHighlight();
        };
    }, [removeHighlight]);
    useEffect(() => {
        if (!selectedSourceId)
            return;
        const source = sources.find(s => s.sourceId === selectedSourceId);
        if (!source)
            return;
        const layers = createHighlightLayers(source, selectedSourceLayerId, selectedSourceLayerLayerId);
        addHighlight(layers);
    }, [selectedSourceId, selectedSourceLayerId, selectedSourceLayerLayerId]);
    const handleChangeSource = useCallback((id) => {
        captureEvent(Event.SelectSource, {
            id,
        });
        changeSource(id);
        changeSourceLayer(null);
        changeSourceLayerLayer(null);
    }, [changeSource, changeSourceLayer, changeSourceLayerLayer, captureEvent]);
    const handleSelectSourceLayer = useCallback((id) => {
        captureEvent(Event.SelectSourceLayer, {
            id,
        });
        changeSourceLayer(id);
        changeSourceLayerLayer(null);
    }, [changeSourceLayer, changeSourceLayerLayer, captureEvent]);
    const handleSelectSourceLayerLayer = useCallback((id) => {
        captureEvent(Event.SelectSourceLayerLayer, {
            id,
        });
        changeSourceLayerLayer(id);
    }, [changeSourceLayerLayer, captureEvent]);
    return (_jsxs(PrimaryPanel, { children: [_jsx(SourceListHeader, { onClose: onClose }), _jsx(ScrollArea, { flex: "1", mih: 0, offsetScrollbars: true, mr: "-8px ", children: _jsxs(Accordion, { variant: "contained", defaultValue: selectedSourceId, value: selectedSourceId, onChange: handleChangeSource, children: [sources.length === 0 && (_jsx(Text, { fz: 14, fs: "italic", c: "dimmed", children: _jsx(FormattedMessage, { id: "text.noSources", defaultMessage: "No sources" }) })), sources.map(({ sourceId, layers, source, tileJSON, metadata }) => {
                            const { type } = source;
                            if (type === "vector") {
                                return (_jsx(VectorSourceAccordionItem, { sourceId: sourceId, layers: layers, tileJSON: tileJSON, metadata: metadata, selectedSourceLayerId: selectedSourceLayerId, onSelectSourceLayer: handleSelectSourceLayer, selectedSourceLayerLayerId: selectedSourceLayerLayerId, onSelectSourceLayerLayer: handleSelectSourceLayerLayer }, sourceId));
                            }
                            if (type === "raster") {
                                return (_jsx(RasterSourceAccordionItem, { sourceId: sourceId, tileJSON: tileJSON, metadata: metadata }, sourceId));
                            }
                            if (type === "geojson") {
                                return (_jsx(GeoJSONSourceAccordionItem, { sourceId: sourceId, layers: layers, metadata: metadata, isInline: typeof source.data !== "string", selectedSourceLayerLayerId: selectedSourceLayerLayerId, onSelectSourceLayerLayer: handleSelectSourceLayerLayer }, sourceId));
                            }
                            return null;
                        })] }) })] }));
});
SourceList.displayName = "SourceList";
